import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../../../components/Button';
import { ConfirmModal } from '../../../components/ConfirmModal';
import { TextInput } from '../../../components/TextInput';
import { ApiError } from '../../../services/api';
import {
  STRIPE_CUSTOMER_SEARCH_MIN_LENGTH,
  backfillStripeInvoices,
  cancelStripeSubscription,
  createStripeSubscription,
  ensureStripeCustomer,
  getStripePaymentMethod,
  getStripeSubscription,
  linkStripeCustomer,
  removeStripePaymentMethod,
  searchStripeCustomers,
  startStripeSetupIntent,
} from '../../../services/admin-billing';
import type {
  BillingPlan,
  BillingStripeBackfill,
  BillingStripeCustomerSearchItem,
  BillingStripePaymentMethod,
  BillingStripeSetupIntent,
  BillingStripeSetupKind,
  BillingStripeSubscription,
  CreateStripeCustomerRequest,
  CreateStripeSubscriptionRequest,
} from '../../../types/adminBilling';
import { BillingSetupChecklist, type BillingSetupStep } from './BillingSetupChecklist';
import { BillingStripeSubscriptionSection } from './BillingStripeSubscriptionSection';
import { StripeCustomerModal } from './StripeCustomerModal';
import { StripeSubscriptionModal } from './StripeSubscriptionModal';
import {
  COLLECTION_METHOD_LABEL,
  PAYMENT_METHOD_KIND_LABEL,
  STRIPE_SETUP_STATE_LABEL,
  instantCell,
  planCellText,
  subscriptionStatusText,
} from './billingFormat';
import { AdminPanel } from '../AdminPage';

interface BillingStripePanelProps {
  organizationId: string;
  /** Prefills the Stripe customer's name, and names the organization in the dialogs. */
  organizationName: string;
  /** Stripe is already the system of record for this organization. */
  isStripeManaged: boolean;
  /**
   * The organization's plan, or null when it has none.
   *
   * It is both a precondition and a source of defaults: the Stripe customer id lives on the plan
   * row, so there is nothing to link to without one, and the amount, interval and payment terms a
   * subscription falls back to are the plan's. Its four cached `stripe…` fields are a **hint**,
   * refreshed whenever the subscription is read; the live read is what this panel shows.
   */
  plan: BillingPlan | null;
  /** Called when something here could have changed the page's totals or its billing mode. */
  onChanged: () => void;
}

const describe = (err: unknown, fallback: string): string =>
  err instanceof ApiError && err.message ? err.message : fallback;

/** Every Stripe route answers 409 while the feature is off, and nothing is written on a refusal. */
const isStripeOff = (err: unknown): err is ApiError =>
  err instanceof ApiError && err.statusCode === 409;

const SETUP_KIND_LABEL: Record<BillingStripeSetupKind, string> = {
  Card: 'card',
  Ach: 'bank account',
};

/** How long the lookup waits after the last keystroke before asking Stripe. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Stripe for one organization: which customer it is pointed at, what is on file, and importing what
 * Stripe already holds.
 *
 * **Linking a customer is the switch.** From the moment it succeeds Stripe is the system of record:
 * this system raises no invoices for the organization and mails it nothing, and its mirrored
 * invoices refuse mark paid and void. So the panel says which side of that line the organization is
 * on before it offers anything.
 *
 * **There is no charge button, and no endpoint behind one.** Collection belongs to Stripe: it
 * auto-charges the method on file, or the customer pays on the hosted invoice page, whose link
 * rides on the invoice row itself. A saved method still matters, which is what `isChargeable`
 * reports, so an admin can see whether the auto-charge will work.
 *
 * **Nothing here collects a card or a bank account.** The setup intent comes back with a client
 * secret for a hosted Stripe step, and that secret is surfaced as a handoff. Building a card form
 * in this app would put card details through our own JavaScript, which is the one thing the whole
 * SetupIntent design exists to avoid.
 *
 * While Stripe is off every route answers 409, so the panel asks once, says plainly that it is off
 * and offers no control that would produce another refusal.
 */
export const BillingStripePanel = ({
  organizationId,
  organizationName,
  isStripeManaged,
  plan,
  onChanged,
}: BillingStripePanelProps) => {
  const hasPlan = plan !== null;
  const stripeInvoicesSyncedAt = plan?.stripeInvoicesSyncedAt ?? null;

  const [method, setMethod] = useState<BillingStripePaymentMethod | null>(null);
  const [offMessage, setOffMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  /**
   * Which action the error above came from.
   *
   * One error string is shared by every control here, so a dialog that showed it unconditionally
   * would report the failure of something the operator did before opening it.
   */
  const [failedAction, setFailedAction] = useState<string | null>(null);
  const [setupIntent, setSetupIntent] = useState<BillingStripeSetupIntent | null>(null);
  const [removeOpen, setRemoveOpen] = useState(false);

  const [search, setSearch] = useState('');
  const [results, setResults] = useState<BillingStripeCustomerSearchItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [customerIdInput, setCustomerIdInput] = useState('');

  const [backfill, setBackfill] = useState<BillingStripeBackfill | null>(null);

  const [subscription, setSubscription] = useState<BillingStripeSubscription | null>(null);
  const [subLoading, setSubLoading] = useState(false);
  const [subError, setSubError] = useState<string | null>(null);
  const [subReloadToken, setSubReloadToken] = useState(0);

  const [customerOpen, setCustomerOpen] = useState(false);
  const [customerMessage, setCustomerMessage] = useState<string | null>(null);
  const [subscriptionOpen, setSubscriptionOpen] = useState(false);
  /** Which cancel the operator is being asked to confirm, or null when neither dialog is open. */
  const [cancelMode, setCancelMode] = useState<'periodEnd' | 'now' | null>(null);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);
  const reloadSubscription = useCallback(() => setSubReloadToken((token) => token + 1), []);

  /*
    Only a different organization blanks the panel. Linking, creating a customer and starting a
    setup all re-read the payment method, and swapping the whole panel for "Loading..." on those
    would take the client secret this screen has just handed the admin off the screen mid-copy. A
    change of organization is the opposite case: this route is not remounted between two
    organizations, so anything held here would otherwise be read as the new one's.
  */
  const loadedOrg = useRef<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    if (loadedOrg.current !== organizationId) {
      setLoading(true);
      setMethod(null);
      setOffMessage(null);
      setSetupIntent(null);
      setActionError(null);
      setBackfill(null);
      setSearch('');
      setResults([]);
      setCustomerIdInput('');
      setCustomerMessage(null);
      setSubscription(null);
      setSubError(null);
    }
    setLoadError(null);

    getStripePaymentMethod(organizationId, controller.signal)
      .then((loaded) => {
        if (controller.signal.aborted) return;
        setMethod(loaded);
        setOffMessage(null);
        loadedOrg.current = organizationId;
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (isStripeOff(err)) {
          // Not an error: the server is telling us the feature is switched off. Everything below
          // would answer the same way, so nothing below is offered.
          setMethod(null);
          setOffMessage(
            err.message || 'Stripe is switched off, so nothing can be set up or linked here.'
          );
        } else {
          setLoadError(describe(err, 'Could not load the Stripe details for this organization.'));
        }
        loadedOrg.current = organizationId;
        setLoading(false);
      });

    return () => controller.abort();
  }, [organizationId, reloadToken]);

  const hasCustomer = method?.hasCustomer ?? false;

  /*
    The subscription is read separately, and only once there is a customer that could have one: an
    organization with no plan or no customer has no subscription by definition, and asking anyway
    spends a round trip to be told so.

    It keeps its own loading, error and retry rather than joining the panel's. Stripe failing on
    this one read must not take the payment method, the link controls and the import button off the
    screen with it - they are exactly what an operator needs when something is wrong at Stripe.
  */
  useEffect(() => {
    if (!hasPlan || !hasCustomer || offMessage !== null) {
      setSubscription(null);
      setSubLoading(false);
      setSubError(null);
      return;
    }

    const controller = new AbortController();
    setSubLoading(true);
    setSubError(null);

    getStripeSubscription(organizationId, controller.signal)
      .then((loaded) => {
        if (controller.signal.aborted) return;
        setSubscription(loaded);
        setSubLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setSubError(
          isStripeOff(err)
            ? err.message || 'Stripe was switched off while this page was open.'
            : describe(err, 'Could not read the subscription from Stripe.')
        );
        setSubLoading(false);
      });

    return () => controller.abort();
  }, [organizationId, hasPlan, hasCustomer, offMessage, subReloadToken]);

  /*
    The lookup runs from the typed term, debounced and abortable. The token is what stops an
    earlier, slower response painting over a later one: the abort covers the common case, but a
    response already in flight when the signal fires still resolves.
  */
  const searchToken = useRef(0);
  const term = search.trim();
  const termTooShort = term.length > 0 && term.length < STRIPE_CUSTOMER_SEARCH_MIN_LENGTH;

  useEffect(() => {
    if (term.length < STRIPE_CUSTOMER_SEARCH_MIN_LENGTH) {
      setResults([]);
      setSearching(false);
      setSearchError(null);
      return;
    }

    const controller = new AbortController();
    const token = searchToken.current + 1;
    searchToken.current = token;
    setSearching(true);
    setSearchError(null);

    const timer = window.setTimeout(() => {
      searchStripeCustomers(term, controller.signal)
        .then((found) => {
          if (searchToken.current !== token) return;
          setResults(found);
          setSearching(false);
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted || searchToken.current !== token) return;
          setResults([]);
          setSearchError(
            isStripeOff(err)
              ? err.message || 'Stripe was switched off while this page was open.'
              : describe(err, 'Could not search Stripe for customers.')
          );
          setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [term]);

  const runAction = useCallback(
    async (name: string, action: () => Promise<void>, fallback: string) => {
      if (pendingAction) return;
      setPendingAction(name);
      setActionError(null);
      setFailedAction(null);
      try {
        await action();
      } catch (err: unknown) {
        setActionError(
          isStripeOff(err)
            ? err.message || 'Stripe was switched off while this page was open.'
            : describe(err, fallback)
        );
        setFailedAction(name);
      } finally {
        setPendingAction(null);
      }
    },
    [pendingAction]
  );

  const handleCreateCustomer = (details: CreateStripeCustomerRequest) =>
    runAction(
      'customer',
      async () => {
        const created = await ensureStripeCustomer(organizationId, details);
        /*
          The server's sentence, not ours. It is the only thing that says whether a customer was
          created from these details or an existing one was linked and the details dropped, and
          guessing from `created` would leave an operator believing an address reached Stripe.
        */
        setCustomerMessage(
          created.message ||
            (created.created
              ? `Created Stripe customer ${created.customerId}.`
              : `Already linked to Stripe customer ${created.customerId}.`)
        );
        setCustomerOpen(false);
        reload();
        // The organization is Stripe managed from here on, which changes the rest of the page.
        onChanged();
      },
      'Could not create the Stripe customer.'
    );

  const handleCreateSubscription = (request: CreateStripeSubscriptionRequest) =>
    runAction(
      'subscription',
      async () => {
        setSubscription(await createStripeSubscription(organizationId, request));
        setSubError(null);
        setSubscriptionOpen(false);
        // Stripe raises this organization's invoices on a schedule from here on.
        onChanged();
      },
      'Could not create the Stripe subscription.'
    );

  const handleCancelSubscription = (atPeriodEnd: boolean) =>
    runAction(
      atPeriodEnd ? 'cancel:periodEnd' : 'cancel:now',
      async () => {
        setSubscription(await cancelStripeSubscription(organizationId, atPeriodEnd));
        setSubError(null);
        setCancelMode(null);
        onChanged();
      },
      'Could not cancel the Stripe subscription.'
    );

  const handleLink = (customerId: string) =>
    runAction(
      `link:${customerId}`,
      async () => {
        await linkStripeCustomer(organizationId, customerId);
        setSearch('');
        setResults([]);
        setCustomerIdInput('');
        setBackfill(null);
        reload();
        onChanged();
      },
      'Could not link that Stripe customer.'
    );

  const handleBackfill = () =>
    runAction(
      'backfill',
      async () => {
        setBackfill(await backfillStripeInvoices(organizationId));
        // The mirror may have gained invoices, so the totals above this panel have moved.
        onChanged();
      },
      'Could not import the invoices from Stripe.'
    );

  const handleSetup = (kind: BillingStripeSetupKind) =>
    runAction(
      kind,
      async () => {
        setSetupIntent(await startStripeSetupIntent(organizationId, kind));
        // The method is not attached yet, but the pending state on the server has moved.
        reload();
      },
      `Could not start the ${SETUP_KIND_LABEL[kind]} setup.`
    );

  const handleRemove = () =>
    runAction(
      'remove',
      async () => {
        const next = await removeStripePaymentMethod(organizationId);
        setMethod(next);
        setSetupIntent(null);
        setRemoveOpen(false);
        onChanged();
      },
      'Could not remove the saved payment method.'
    );

  const showPending =
    method !== null && (method.pendingSetupState !== 'None' || method.pendingSetupMessage !== null);

  /*
    The four things an organization needs, in the order they have to arrive.

    Only the plan, the customer and the subscription are ever "not yet": a payment method is
    optional, because collection defaults to Stripe emailing the invoice and that needs nothing on
    file. Marking it outstanding would send an operator off to attach a card that the organization
    may never need, ahead of the subscription that is the thing actually missing.
  */
  const steps: BillingSetupStep[] = [
    plan
      ? {
          key: 'plan',
          name: 'Billing plan',
          state: 'done',
          detail: planCellText(plan.amountCents, plan.currency, plan.interval),
        }
      : {
          key: 'plan',
          name: 'Billing plan',
          state: 'todo',
          detail: 'No plan yet.',
          action: 'Save a plan above. The Stripe customer is recorded on it, so nothing here works without one.',
        },
    hasCustomer
      ? {
          key: 'customer',
          name: 'Stripe customer',
          state: 'done',
          detail: method?.customerId ?? 'Linked.',
        }
      : {
          key: 'customer',
          name: 'Stripe customer',
          state: 'todo',
          detail: 'Not linked yet, so Stripe holds nothing for this organization.',
          action: 'Create a Stripe customer, or link one that already exists.',
        },
    method?.isChargeable
      ? {
          key: 'method',
          name: 'Payment method',
          state: 'done',
          detail: method.label ?? PAYMENT_METHOD_KIND_LABEL[method.kind],
        }
      : {
          key: 'method',
          name: 'Payment method',
          state: 'optional',
          detail:
            method && method.pendingSetupState !== 'None'
              ? STRIPE_SETUP_STATE_LABEL[method.pendingSetupState]
              : 'Nothing on file. Only needed for Stripe to charge automatically.',
        },
    subscription?.hasSubscription
      ? {
          key: 'subscription',
          name: 'Subscription',
          state: 'done',
          detail: subscription.collectionMethod
            ? `${subscriptionStatusText(subscription.status)}, ${COLLECTION_METHOD_LABEL[
                subscription.collectionMethod
              ].toLowerCase()}`
            : subscriptionStatusText(subscription.status),
        }
      : /*
          Not known yet covers the gap between the customer arriving and the read that answers for
          it starting. The condition mirrors the effect's own guard on purpose: wherever a read is
          going to happen, this step must not assert there is no subscription before it has looked.
          It did, for a frame, and "None, so nothing raises invoices on a schedule" is the one line
          here that must never be shown on a guess.

          An organization with no plan or no customer is the other case: no read is coming, so it
          genuinely has no subscription and the step says so.
        */
        subLoading || subError !== null || (hasPlan && hasCustomer && subscription === null)
        ? {
            key: 'subscription',
            name: 'Subscription',
            state: 'unknown',
            detail: subError
              ? 'Stripe could not be read just now.'
              : 'Reading it from Stripe...',
          }
        : {
            key: 'subscription',
            name: 'Subscription',
            state: 'todo',
            detail: 'None, so nothing raises invoices on a schedule.',
            action: hasCustomer
              ? 'Create the subscription below.'
              : 'Create the Stripe customer first.',
          },
  ];

  /*
    Only said once the organization is Stripe managed. Before that this system raises its invoices
    from the plan, so "nothing raises invoices" would be plainly untrue.
  */
  const setupWarning =
    hasCustomer && subscription !== null && !subscription.hasSubscription
      ? 'Stripe bills this organization, and without a subscription nothing raises its invoices: not Stripe on a schedule, and not this system. Anything it has been charged so far was raised by hand in the Stripe dashboard.'
      : null;

  return (
    <AdminPanel
      title="Stripe"
      titleId="billing-stripe-title"
      description={
        isStripeManaged
          ? 'Stripe bills this organization. It raises the invoices, it chases them, and this system mirrors what it holds.'
          : 'This organization is billed by RosterPilot. Linking a Stripe customer hands the invoices and the chasing to Stripe.'
      }
    >

      {loading ? (
        <p className="admin-state">Loading the Stripe details...</p>
      ) : offMessage ? (
        <p className="billing-panel__note" data-testid="billing-stripe-off">
          {offMessage} Turn it on in billing settings once the Stripe keys are configured on the
          server. Until then there is nothing to link and nothing to import.
        </p>
      ) : loadError ? (
        <div className="admin-error" role="alert">
          <span>{loadError}</span>
          <Button variant="secondary" onClick={reload} data-testid="billing-stripe-retry">
            Try again
          </Button>
        </div>
      ) : method ? (
        <>
          <BillingSetupChecklist steps={steps} warning={setupWarning} />

          <dl className="billing-panel__facts">
            <div className="billing-panel__fact">
              <dt className="billing-panel__fact-label">Stripe customer</dt>
              <dd className="billing-panel__fact-value" data-testid="billing-stripe-customer">
                {method.hasCustomer ? (method.customerId ?? 'Linked') : 'Not linked yet'}
              </dd>
            </div>
            <div className="billing-panel__fact">
              <dt className="billing-panel__fact-label">Saved method</dt>
              <dd className="billing-panel__fact-value" data-testid="billing-stripe-method">
                {method.label ?? PAYMENT_METHOD_KIND_LABEL[method.kind]}
              </dd>
            </div>
            <div className="billing-panel__fact">
              <dt className="billing-panel__fact-label">Stripe can auto-charge</dt>
              <dd
                className={`billing-panel__fact-value${
                  method.isChargeable ? '' : ' billing-panel__fact-value--danger'
                }`}
                data-testid="billing-stripe-chargeable"
              >
                {method.isChargeable ? 'Yes' : 'Not yet'}
              </dd>
            </div>
            <div className="billing-panel__fact">
              <dt className="billing-panel__fact-label">Invoices last imported</dt>
              <dd className="billing-panel__fact-value" data-testid="billing-stripe-synced">
                {stripeInvoicesSyncedAt ? instantCell(stripeInvoicesSyncedAt) : 'Never'}
              </dd>
            </div>
          </dl>

          {showPending && (
            <div className="billing-panel__banner billing-panel__banner--warning">
              <p className="billing-panel__reminder-outcome" data-testid="billing-stripe-pending">
                {STRIPE_SETUP_STATE_LABEL[method.pendingSetupState]}
                {method.pendingSetupKind !== 'None'
                  ? ` (${PAYMENT_METHOD_KIND_LABEL[method.pendingSetupKind]})`
                  : ''}
              </p>
              {method.pendingSetupMessage && (
                <p
                  className="billing-panel__reminder-reason"
                  data-testid="billing-stripe-pending-message"
                >
                  {method.pendingSetupMessage}
                </p>
              )}
              {method.pendingSetupState === 'PendingMicrodeposits' && (
                <p className="billing-panel__note" data-testid="billing-stripe-microdeposits">
                  Stripe sends two small deposits to the account. The college confirms both amounts
                  on Stripe&apos;s own page, not on this screen, and nothing can be charged until
                  they do.
                </p>
              )}
            </div>
          )}

          {actionError && (
            <p
              className="admin-error"
              role="alert"
              data-testid="billing-stripe-error"
            >
              {actionError}
            </p>
          )}

          {/*
            The server's own sentence about the customer. It stays on screen after the dialog
            closes, because the one thing it has to be able to say - "this organization was already
            linked, so the details you typed were not applied" - is easiest to miss and impossible
            to work out from anything else on the panel.
          */}
          {customerMessage && (
            <p
              className="billing-panel__banner"
              role="status"
              data-testid="billing-stripe-customer-message"
            >
              {customerMessage}
            </p>
          )}

          {!method.hasCustomer ? (
            <div className="billing-stripe__link" data-testid="billing-stripe-link">
              <h4 className="billing-panel__subtitle">Link a Stripe customer</h4>
              {!hasPlan ? (
                <p className="billing-panel__note" data-testid="billing-stripe-needs-plan">
                  The customer is recorded on the billing plan, so give this organization a plan
                  first. Nothing above can be linked until it has one.
                </p>
              ) : (
                <>
                  <p className="billing-panel__note">
                    Linking hands this organization&apos;s billing to Stripe. From then on Stripe
                    raises the invoices and chases them, and nothing is invoiced or emailed from
                    here. Pick the customer by its id: names at Stripe are not unique, and a
                    near-match would mirror one college&apos;s invoices against another.
                  </p>

                  <TextInput
                    label="Search Stripe by name or email"
                    type="search"
                    placeholder="At least three characters"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    disabled={pendingAction !== null}
                    data-testid="billing-stripe-search"
                  />

                  {/* Always mounted, so what the search found is announced when it appears. */}
                  <div role="status" className="billing-panel__note">
                    {termTooShort
                      ? `Type at least ${STRIPE_CUSTOMER_SEARCH_MIN_LENGTH} characters to search.`
                      : searching
                        ? 'Searching Stripe...'
                        : term.length >= STRIPE_CUSTOMER_SEARCH_MIN_LENGTH &&
                            results.length === 0 &&
                            !searchError
                          ? 'No Stripe customer matches that.'
                          : ''}
                  </div>

                  {searchError && (
                    <p
                      className="admin-error"
                      role="alert"
                      data-testid="billing-stripe-search-error"
                    >
                      {searchError}
                    </p>
                  )}

                  {results.length > 0 && (
                    <ul
                      className="billing-panel__contact-list"
                      data-testid="billing-stripe-results"
                    >
                      {results.map((result) => (
                        <li key={result.customerId} className="billing-panel__contact">
                          <span className="billing-panel__contact-main">
                            <span className="billing-panel__contact-email">
                              {result.name ?? result.customerId}
                            </span>
                            <span className="billing-panel__contact-meta">
                              {result.customerId}
                              {result.email ? ` - ${result.email}` : ''}
                            </span>
                            {/*
                              One Stripe customer belongs to one organization, so a row already
                              taken is shown and refused here rather than at the API.
                            */}
                            {result.linkedOrganizationName && (
                              <span
                                className="billing-panel__contact-meta"
                                data-testid={`billing-stripe-taken-${result.customerId}`}
                              >
                                Already linked to {result.linkedOrganizationName}
                              </span>
                            )}
                          </span>
                          <Button
                            variant="secondary"
                            onClick={() => handleLink(result.customerId)}
                            loading={pendingAction === `link:${result.customerId}`}
                            disabled={
                              pendingAction !== null || result.linkedOrganizationName !== null
                            }
                            data-testid={`billing-stripe-link-${result.customerId}`}
                          >
                            Link
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}

                  <TextInput
                    label="Or paste a customer id"
                    placeholder="cus_..."
                    value={customerIdInput}
                    onChange={(e) => setCustomerIdInput(e.target.value)}
                    disabled={pendingAction !== null}
                    data-testid="billing-stripe-customer-id"
                  />

                  <div className="billing-panel__actions">
                    <Button
                      onClick={() => handleLink(customerIdInput.trim())}
                      loading={pendingAction === `link:${customerIdInput.trim()}`}
                      disabled={pendingAction !== null || customerIdInput.trim().length === 0}
                      data-testid="billing-stripe-link-pasted"
                    >
                      Link this customer
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => setCustomerOpen(true)}
                      disabled={pendingAction !== null}
                      data-testid="billing-stripe-create-customer"
                    >
                      Create a new customer instead
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <>
              <div className="billing-stripe__backfill">
                <h4 className="billing-panel__subtitle">Import what Stripe already holds</h4>
                <p className="billing-panel__note">
                  Brings in every invoice Stripe has for this customer. Safe to press twice: it
                  updates the same rows rather than duplicating them, and a customer with no
                  invoices is an ordinary answer. Webhooks keep everything current afterwards.
                </p>
                <div className="billing-panel__actions">
                  <Button
                    onClick={handleBackfill}
                    loading={pendingAction === 'backfill'}
                    disabled={pendingAction !== null}
                    data-testid="billing-stripe-backfill"
                  >
                    Import invoices from Stripe
                  </Button>
                </div>
                {backfill && (
                  <div
                    className="billing-panel__banner"
                    role="status"
                    data-testid="billing-stripe-backfill-result"
                  >
                    <p className="billing-panel__reminder-outcome">{backfill.message}</p>
                    <p className="billing-panel__note">
                      {backfill.imported} imported, {backfill.updated} updated,{' '}
                      {backfill.unchanged} already current, out of {backfill.totalAtStripe} at
                      Stripe.
                    </p>
                  </div>
                )}
              </div>

              <div className="billing-panel__actions">
                <Button
                  variant="secondary"
                  onClick={() => handleSetup('Card')}
                  loading={pendingAction === 'Card'}
                  disabled={pendingAction !== null}
                  data-testid="billing-stripe-setup-card"
                >
                  Set up a card
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => handleSetup('Ach')}
                  loading={pendingAction === 'Ach'}
                  disabled={pendingAction !== null}
                  data-testid="billing-stripe-setup-ach"
                >
                  Set up a bank account
                </Button>
                {method.paymentMethodId && (
                  <Button
                    variant="secondary"
                    onClick={() => setRemoveOpen(true)}
                    disabled={pendingAction !== null}
                    data-testid="billing-stripe-remove"
                  >
                    Remove saved method
                  </Button>
                )}
              </div>

              <BillingStripeSubscriptionSection
                subscription={subscription}
                error={subError}
                plan={plan}
                busy={pendingAction !== null}
                cancelling={
                  pendingAction === 'cancel:periodEnd'
                    ? 'periodEnd'
                    : pendingAction === 'cancel:now'
                      ? 'now'
                      : null
                }
                creating={pendingAction === 'subscription'}
                onCreate={() => setSubscriptionOpen(true)}
                onRequestCancel={(atPeriodEnd) =>
                  setCancelMode(atPeriodEnd ? 'periodEnd' : 'now')
                }
                onRetry={reloadSubscription}
              />
            </>
          )}

          {setupIntent && (
            <div className="billing-stripe__handoff" data-testid="billing-stripe-handoff">
              <p className="billing-panel__reminder-outcome">
                A {SETUP_KIND_LABEL[setupIntent.kind]} setup has been started.
              </p>
              <p className="billing-panel__note">
                Finish it on Stripe, not here. This screen never asks for a card or an account
                number; the secret below is what a Stripe-hosted form confirms against, and it is
                good for this one setup only.
              </p>
              <TextInput
                label="Setup intent client secret"
                readOnly
                value={setupIntent.clientSecret}
                data-testid="billing-stripe-client-secret"
              />
              {setupIntent.hostedVerificationUrl && (
                <p className="billing-panel__note">
                  <a
                    href={setupIntent.hostedVerificationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="billing-stripe-hosted-url"
                  >
                    Open the Stripe page for this setup
                  </a>
                </p>
              )}
            </div>
          )}
        </>
      ) : null}

      <StripeCustomerModal
        isOpen={customerOpen}
        organizationName={organizationName}
        saving={pendingAction === 'customer'}
        error={failedAction === 'customer' ? actionError : null}
        onCreate={handleCreateCustomer}
        onClose={() => setCustomerOpen(false)}
      />

      <StripeSubscriptionModal
        isOpen={subscriptionOpen}
        organizationName={organizationName}
        plan={plan}
        /*
          The one field this may be driven from. A stored method that Stripe cannot charge - an ACH
          mandate still in microdeposit verification has an id and a label - would otherwise put
          ChargeAutomatically on the menu, and the API answers 409.

          It defaults to false while the payment method is still loading, which is the safe
          direction: the worst case is an operator who has to reopen the dialog.
        */
        canChargeAutomatically={method?.isChargeable ?? false}
        paymentMethodLabel={method?.label ?? null}
        saving={pendingAction === 'subscription'}
        error={failedAction === 'subscription' ? actionError : null}
        onCreate={handleCreateSubscription}
        onClose={() => setSubscriptionOpen(false)}
      />

      {/*
        Two dialogs rather than one with a toggle. They are different acts with different
        consequences - one lets a period the customer has already paid for run out, the other takes
        it away - and a single dialog whose meaning turns on a checkbox is how the wrong one gets
        confirmed.
      */}
      <ConfirmModal
        isOpen={cancelMode === 'periodEnd'}
        onClose={() => setCancelMode(null)}
        onConfirm={() => handleCancelSubscription(true)}
        busy={pendingAction === 'cancel:periodEnd'}
        title="Stop billing at the end of this period?"
        message={`Stripe finishes the period ${organizationName} has already been invoiced for and then raises nothing further. Nothing is refunded and nothing is taken away: the subscription keeps running until the period ends.`}
        confirmText="Stop at period end"
        cancelText="Keep billing"
      >
        {/*
          The reason goes inside the dialog, because a refused cancel leaves the dialog open and the
          panel's own error banner is behind the overlay. An operator who cannot see why it was
          refused presses the button again, which is the one thing this pair of controls must never
          invite.
        */}
        {failedAction === 'cancel:periodEnd' && actionError && (
          <p className="billing-form__error" role="alert" data-testid="billing-stripe-cancel-error">
            {actionError}
          </p>
        )}
      </ConfirmModal>

      <ConfirmModal
        isOpen={cancelMode === 'now'}
        onClose={() => setCancelMode(null)}
        onConfirm={() => handleCancelSubscription(false)}
        busy={pendingAction === 'cancel:now'}
        title="Stop the recurring invoice immediately?"
        message={`This ends billing for ${organizationName} right now, in the middle of a period it has already been invoiced for. That time is forfeited and Stripe refunds nothing. Use "stop at period end" unless this subscription was created by mistake.`}
        confirmText="End it now"
        cancelText="Keep billing"
      >
        {failedAction === 'cancel:now' && actionError && (
          <p className="billing-form__error" role="alert" data-testid="billing-stripe-cancel-error">
            {actionError}
          </p>
        )}
      </ConfirmModal>

      <ConfirmModal
        isOpen={removeOpen}
        onClose={() => setRemoveOpen(false)}
        onConfirm={handleRemove}
        busy={pendingAction === 'remove'}
        title="Remove the saved payment method?"
        message={`This detaches ${
          method?.label ?? 'the saved method'
        } at Stripe. Stripe cannot auto-charge this organization afterwards until a new one is set up, and it carries on chasing anything outstanding.`}
        confirmText="Remove method"
        cancelText="Keep it"
      />
    </AdminPanel>
  );
};
